import React from 'react';
import { Form, Formik } from 'formik';
import { WizardFormProps } from '../../../shared/WizardFormProps';
import * as yup from 'yup';
import { SubHeaderTextTW } from '../../../../../../../tailwindComponents/subHeaderText/SubHeaderTextTW';
import { TextAreaFieldTW } from '../../../../../../../tailwindComponents/textAreaTW/TextAreaFieldTW';
import { Show } from '../../../../../../shared/Show';
import { BodyTextTW } from '../../../../../../../tailwindComponents/bodyText/BodyTextTW';
import { DEFAULT_RATE_VALUE } from '../../DEFAULT_RATE_VALUE';
import { useDetermineNotesFormProps } from '../useDetermineNotesFormProps';
import { NotesNotarySection } from './NotesNotarySection';
import { CreateSigningAppointmentFormData } from '../../../04_confirmationStep/convertToCreateSigningAppointmentRequest';
import { eSigningMethod } from 'types/signingAppointment/eSigningMethod';
import { isNil } from 'lodash';

const emptyNotary = (values: INotesFormData): boolean => {
  if (!values.notary) {
    return true;
  }
  return values.notary.firstName === '' && values.notary.lastName === '';
};

export const NotesForm: React.FC<WizardFormProps> = ({
  advance,
  children,
  updatedWizardFormData,
  setUpdatedWizardFormData,
}) => {
  const { title, placeholder, hasNotary, formTitle = '' } = useDetermineNotesFormProps(updatedWizardFormData);
  const isDigitalScheduling =
    updatedWizardFormData.isDigitalScheduling || updatedWizardFormData.isDigitalSchedulingDraft;
  return (
    <Formik
      initialValues={buildInitialFormData(updatedWizardFormData, hasNotary)}
      validationSchema={
        isDigitalScheduling || updatedWizardFormData.signingMethod === eSigningMethod.wetSign
          ? notesDigSyncValidationSchema
          : notesValidationSchema
      }
      onSubmit={(values: INotesFormData) => {
        if (emptyNotary(values)) {
          values.notary = undefined;
          values.rateCents = undefined;
        }
        setUpdatedWizardFormData((prevState) => ({ ...prevState, ...values }));
        advance();
      }}
      validateOnMount={true}
    >
      {({ values }) => (
        <Form>
          <div className="flex justify-start p-2">
            <SubHeaderTextTW className={'mr-5'}>{title}</SubHeaderTextTW>
          </div>
          <div className="p-2">
            <TextAreaFieldTW name="notes" maxLength={250} placeholder={placeholder} />
            <Show when={!isNil(values.notes)}>
              <BodyTextTW className={'text-red-800'}>
                Characters remaining: {250 - (values.notes?.length ?? 0)}
              </BodyTextTW>
            </Show>
          </div>
          <NotesNotarySection
            hasNotary={hasNotary}
            formTitle={formTitle}
            updatedWizardFormData={updatedWizardFormData}
          />
          {children}
        </Form>
      )}
    </Formik>
  );
};

export type INotesFormData = Pick<CreateSigningAppointmentFormData, 'notes' | 'notary' | 'rateCents'>;

const buildInitialFormData = (wizardFormData: CreateSigningAppointmentFormData, hasNotary: boolean): INotesFormData => {
  if (hasNotary) {
    return {
      notes: wizardFormData.notes,
      notary: wizardFormData.notary,
      rateCents: wizardFormData.rateCents ?? DEFAULT_RATE_VALUE,
    };
  }

  return {
    notes: wizardFormData.notes,
  };
};

const notesValidationSchema = yup.object().shape({
  notary: yup.object().shape({
    firstName: yup.string().required('Notary First Name is required').typeError('Notary First Name is required'),
    lastName: yup.string().required('Notary Last Name is required').typeError('Notary Last Name is required'),
    email: yup.string().email(),
  }),
  notes: yup.string().nullable().max(250, 'Notes must be fewer than 250 characters'),
  rateCents: yup.number().positive().nullable(),
});

const notesDigSyncValidationSchema = yup.object().shape({
  notary: yup.object().shape({
    firstName: yup.string(),
    lastName: yup.string(),
    email: yup.string().email(),
  }),
  notes: yup.string().nullable().max(250, 'Notes must be fewer than 250 characters'),
  rateCents: yup.number().positive().nullable(),
});
